import { Eye } from 'lucide-react'
import type { ProposalActivitySummaryDto } from '@lib/dto/proposal-activity.dto'
import { PROPOSAL_CHAPTER_LABELS } from '@lib/enums/proposal-chapter.enum'
import { PROPOSAL_PLAN_LABELS } from '@lib/enums/proposal-plan.enum'
import { formatDateTime } from '@lib/helpers/format'
import { formatMoneyWhole, parseMoney } from '@lib/helpers/money'
import { useProposalActivity } from '@/hooks/useProposalActivity'

/** How many changes to list before "and N more". */
const SHOWN_ADJUSTMENTS = 5

/** "45s", "4 min", "1 h 12 min": time read, at the precision a call needs. */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${String(minutes)} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${String(hours)} h` : `${String(hours)} h ${String(rest)} min`
}

/** "$9,000" from a typed budget, or the text as sent when it is not money. */
function formatBudget(amount: string): string {
  const cents = parseMoney(amount)
  return cents === null ? amount : formatMoneyWhole(cents)
}

function hasAnything(summary: ProposalActivitySummaryDto): boolean {
  return summary.opens > 0 || summary.recent.length > 0
}

/** One labeled line of what they did, left out when there is nothing to say. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs">
      <dt className="w-28 shrink-0 text-muted-text">{label}</dt>
      <dd className="min-w-0 text-secondary-text">{children}</dd>
    </div>
  )
}

/**
 * Chapters by time spent, as a small bar list.
 *
 * Bars against the longest chapter, not the total: the question is which
 * part held their attention, and a share of the whole flattens every bar
 * into a sliver when one chapter dominates.
 */
function ChapterBars({ chapters }: { chapters: ProposalActivitySummaryDto['chapters'] }) {
  const longest = Math.max(1, ...chapters.map((chapter) => chapter.seconds))
  return (
    <ul className="space-y-1" aria-label="Time spent by chapter">
      {chapters.map((chapter) => (
        <li key={chapter.chapter} className="flex items-center gap-2 text-xs">
          <span className="w-32 shrink-0 truncate text-secondary-text">
            {PROPOSAL_CHAPTER_LABELS[chapter.chapter]}
          </span>
          <span className="h-2 min-w-0 flex-1 rounded-sm bg-subtle" aria-hidden>
            <span
              className="block h-full rounded-sm bg-brand"
              style={{ width: `${String(Math.max(2, (chapter.seconds / longest) * 100))}%` }}
            />
          </span>
          <span className="w-14 shrink-0 text-right tabular-nums text-muted-text">
            {formatDuration(chapter.seconds)}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** The summary itself, separate from loading so it can be tested on data. */
export function ProposalActivityView({
  summary,
}: {
  summary: ProposalActivitySummaryDto
}) {
  if (!hasAnything(summary)) {
    return (
      <p className="text-xs text-muted-text">
        No activity yet. It appears here once the client opens the link.
      </p>
    )
  }

  const opens = `Opened ${String(summary.opens)} ${summary.opens === 1 ? 'time' : 'times'}`
  const first =
    summary.firstOpenedAt === null ? '' : ` · first ${formatDateTime(summary.firstOpenedAt)}`
  // One visit has one moment; saying it twice reads like two.
  const seen =
    summary.lastSeenAt === null || summary.lastSeenAt === summary.firstOpenedAt
      ? ''
      : ` · last seen ${formatDateTime(summary.lastSeenAt)}`
  const read =
    summary.totalSeconds === 0 ? '' : ` · ${formatDuration(summary.totalSeconds)} reading`
  const flags = [
    summary.printed ? 'Printed' : null,
    summary.shared ? 'Shared' : null,
    summary.presented ? 'Presented' : null,
  ].filter((flag): flag is string => flag !== null)
  const moreChanges = summary.adjustments.length - SHOWN_ADJUSTMENTS

  return (
    <div className="space-y-2.5">
      <p className="text-xs text-secondary-text">
        {opens}
        {first}
        {seen}
        {read}
      </p>

      {flags.length === 0 ? null : (
        <p className="flex flex-wrap gap-1">
          {flags.map((flag) => (
            <span
              key={flag}
              className="rounded-full bg-pill-blue px-2 py-0.5 text-xs text-pill-blue-text"
            >
              {flag}
            </span>
          ))}
        </p>
      )}

      {summary.chapters.length === 0 ? null : <ChapterBars chapters={summary.chapters} />}

      <dl className="space-y-1">
        {summary.plansTried.length === 0 ? null : (
          <Fact label="Plans tried">
            {summary.plansTried
              .map((entry) =>
                entry.count > 1
                  ? `${PROPOSAL_PLAN_LABELS[entry.plan]} ×${String(entry.count)}`
                  : PROPOSAL_PLAN_LABELS[entry.plan],
              )
              .join(', ')}
          </Fact>
        )}
        {summary.budgets.length === 0 ? null : (
          <Fact label="Budgets typed">
            {summary.budgets.map((entry) => formatBudget(entry.amount)).join(', ')}
          </Fact>
        )}
        {summary.adjustments.length === 0 ? null : (
          <Fact label="Changes tried">
            {summary.adjustments
              .slice(0, SHOWN_ADJUSTMENTS)
              .map((entry) => entry.text)
              .join('; ')}
            {moreChanges > 0 ? `; and ${String(moreChanges)} more` : ''}
          </Fact>
        )}
        {summary.treesOpened.length === 0 ? null : (
          <Fact label="Trees opened">
            {summary.treesOpened
              .map((entry) =>
                entry.count > 1
                  ? `#${entry.label.replace(/^#/, '')} (${String(entry.count)})`
                  : `#${entry.label.replace(/^#/, '')}`,
              )
              .join(', ')}
          </Fact>
        )}
        {summary.videosWatched.length === 0 ? null : (
          <Fact label="Videos watched">
            {summary.videosWatched
              .map((label) => `#${label.replace(/^#/, '')}`)
              .join(', ')}
          </Fact>
        )}
      </dl>
    </div>
  )
}

/**
 * What the client looked at on their shared link, for the follow-up call.
 *
 * "Sam read the plan for four minutes, tried Hazards first twice and typed a
 * $9,000 budget" is a better opening than "did you get a chance to look?".
 * Shown only once a link has been sent; a draft has no reader.
 */
export function ProposalActivityPanel({
  versionId,
  hasLink,
}: {
  versionId: string
  hasLink: boolean
}) {
  const activity = useProposalActivity(hasLink ? versionId : '')
  if (!hasLink) return null

  return (
    <div className="space-y-2 rounded-md border border-default bg-surface px-3 py-2.5">
      <p className="flex items-center gap-1.5 text-sm font-medium">
        <Eye className="h-3.5 w-3.5 text-muted-text" />
        What the client looked at
      </p>
      {activity.isPending ? (
        <p className="text-xs text-muted-text">Loading…</p>
      ) : null}
      {activity.isError ? (
        <p className="text-xs text-muted-text">
          The client’s activity could not be loaded.
        </p>
      ) : null}
      {activity.data === undefined ? null : (
        <ProposalActivityView summary={activity.data} />
      )}
    </div>
  )
}
